/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import apiClient from '../apiClient';
import {
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import {
    DeviceDetailsDto,
    DevicesListDto,
    DevicesListRequestParams,
    DevicesListExcelRequestParams,
    RegistrationHistoryItemDto,
    FirmwareHistoryListDto,
    FirmwareHistoryQueryParams,
    StatusHistoryQueryParams,
    StatusHistoryListDto,
    OperatorHistoryQueryParams,
    OperatorHistoryListDto,
} from './devicesClient.typing';
import { DevicesListTableData } from '../../../components/RegistryWidgets/Devices/DevicesList/DevicesListConfig';
import { RequestToCreateExcelDto } from '../downloadings/downloadingsClient.typings';

const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof DevicesListTableData]?: string } = {
    factoryNumber: 'deviceFactoryNumbers',
    cashBoxTaxNumber: 'cashBoxTaxNumbers',
    cashRegisterFactoryNumber: 'cashRegisterFactoryNumbers',
    storeTaxNumber: 'storeTaxNumbers',
    companyTaxNumber: 'companyTaxNumbers',
};

const getSearchParamName = (searchType: keyof DevicesListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

async function listDevices(params: DevicesListRequestParams): Promise<DevicesListDto> {
    const { search_type, search_text, usbTypes } = params;
    delete params.search_text;
    delete params.search_type;
    delete params.usbTypes;

    const queryParams = {
        ...params,
        [getSearchParamName(search_type as keyof DevicesListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
        usbTypes: usbTypes === null ? [] : [usbTypes],
    };
    return apiClient.post<DevicesListDto>('1.0.0/devices', cutEmptyParamsIncludingArrays(queryParams));
}

async function downloadDevicesAsExcel(params: DevicesListExcelRequestParams): Promise<RequestToCreateExcelDto> {
    const { search_type, search_text, usbTypes } = params;
    delete params.search_text;
    delete params.search_type;
    delete params.usbTypes;

    const queryParams = {
        ...params,
        [getSearchParamName(search_type as keyof DevicesListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
        usbTypes: usbTypes === null ? [] : [usbTypes],
    };

    return apiClient.post<RequestToCreateExcelDto>('1.0.0/devices/excel', cutEmptyParamsIncludingArrays(queryParams));
}

async function getDevice(deviceFactoryNumber: string): Promise<DeviceDetailsDto> {
    return apiClient.get<DeviceDetailsDto>(`1.0.0/devices/${deviceFactoryNumber}`);
}

async function getFirmwareHistory(
    factoryNumber: string,
    params: FirmwareHistoryQueryParams,
): Promise<FirmwareHistoryListDto> {
    return apiClient.get<FirmwareHistoryListDto>(`1.0.0/devices/${factoryNumber}/firmwareVersionHistory`, params);
}

async function downloadFirmwareHistoryAsExcel(factoryNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/devices/${factoryNumber}/firmwareVersionHistory/excel`);
}

async function getStatusHistory(
    factoryNumber: string,
    params: StatusHistoryQueryParams,
): Promise<StatusHistoryListDto> {
    return apiClient.get<StatusHistoryListDto>(`1.0.0/devices/${factoryNumber}/statusHistory`, params);
}

async function downloadStatusHistoryAsExcel(factoryNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/devices/${factoryNumber}/statusHistory/excel`);
}

async function getOperatorHistory(
    factoryNumber: string,
    params: OperatorHistoryQueryParams,
): Promise<OperatorHistoryListDto> {
    return apiClient.get<OperatorHistoryListDto>(`1.0.0/devices/${factoryNumber}/operatorHistory`, params);
}

async function downloadOperatorHistoryAsExcel(factoryNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/devices/${factoryNumber}/operatorHistory/excel`);
}

async function getRegistrationHistory(factoryNumber: string): Promise<RegistrationHistoryItemDto[]> {
    return apiClient.get<RegistrationHistoryItemDto[]>(`1.0.0/devices/${factoryNumber}/registrationHistory`);
}

async function downloadRegistrationHistoryAsExcel(factoryNumber: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/devices/${factoryNumber}/registrationHistory/excel`);
}

export const devicesClient = {
    listDevices,
    downloadDevicesAsExcel,
    getDevice,
    getFirmwareHistory,
    downloadFirmwareHistoryAsExcel,
    getStatusHistory,
    downloadStatusHistoryAsExcel,
    getOperatorHistory,
    downloadOperatorHistoryAsExcel,
    getRegistrationHistory,
    downloadRegistrationHistoryAsExcel,
};
